import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  AlertCircle,
  Edit3,
  FileText,
  UploadCloud,
  RefreshCw,
  Trash2,
  FileCheck,
  ExternalLink,
  Image as ImageIcon,
  BookOpen,
  Book,
  MapPin,
  Calendar,
  Download,
} from "lucide-react";
import { fixMojibake } from "../../utils/downLoadUtils";
import { ConfirmModal } from "../base/BaseModal";
import VietnameseDatePicker from "./VietnameseDatePicker";
import ManageableDocTypeSelect from "./ManageableDocTypeSelect";
import CustomSelect from "../base/CustomSelect";

export const DEFAULT_HISTORY_SUB_CATEGORIES = [];

const PHAM_VI_OPTIONS = [
  { value: "CONG_KHAI", label: "Công khai" },
  { value: "NOI_BO", label: "Nội bộ" },
];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["blockquote", "code-block"],
    [{ direction: "rtl" }],
    ["link"],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "align",
  "list",
  "bullet",
  "check",
  "indent",
  "blockquote",
  "code-block",
  "direction",
  "link",
];

export default function DocumentFormModal({
  onBack,
  onSubmit,
  onSaveDraft,
  subCategories = [],
  isLoading = false,
  initialData = null,
  isEditMode = false,
}) {
  const [formData, setFormData] = useState({
    tieuDe: "",
    tenDiTich: "",
    diaChi: "",
    idDanhMuc: "",
    ngayBanHanh: "",
    phamVi: "CONG_KHAI",
    moTa: "",
    tags: "",
    noiDung: "",
  });

  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [file, setFile] = useState(null);
  const [images, setImages] = useState([]);
  const [errors, setErrors] = useState({});
  const [existingFiles, setExistingFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [dateDisplay, setDateDisplay] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [contentInputMode, setContentInputMode] = useState("editor"); // "editor" | "file"
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [isDraggingImages, setIsDraggingImages] = useState(false);
  const fileInputRef = useRef(null);
  const imagesInputRef = useRef(null);
  const dateInputRef = useRef(null);
  const hasUnsavedDataRef = useRef(false);
  const splitContainerRef = useRef(null);
  const isDragging = useRef(false);
  const [leftWidth, setLeftWidth] = useState(50);

  useEffect(() => {
    if (isEditMode && initialData) {
      const iso = initialData.ngay_ban_hanh
        ? initialData.ngay_ban_hanh.split("T")[0]
        : "";
      setFormData({
        tieuDe: initialData.tieu_de || "",
        tenDiTich: initialData.ten_di_tich || "",
        diaChi: initialData.dia_chi || "",
        idDanhMuc: initialData.thu_vien_danh_muc?.id || "",
        ngayBanHanh: iso,
        phamVi: initialData.pham_vi || "CONG_KHAI",
        moTa: initialData.mo_ta || "",
        noiDung: initialData.noi_dung || "",
        tags:
          initialData.thu_vien_tai_lieu_tag
            ?.map((t) => t.thu_vien_tag?.ten)
            .join(", ") || "",
      });
      setDateDisplay(iso ? iso.split("-").reverse().join("/") : "");
      setTagInput("");
      setExistingFiles(initialData.thu_vien_tai_lieu_file || []);
      setExistingImages(
        initialData.thu_vien_tai_lieu_media?.filter(
          (m) => m.loai === "IMAGE",
        ) || [],
      );
      if (initialData.thu_vien_tai_lieu_file?.length > 0 && !initialData.noi_dung) {
        setContentInputMode("file");
      } else {
        setContentInputMode("editor");
      }
    }
  }, [isEditMode, initialData]);

  const getFullFileUrl = (path) => {
    if (!path) return "";
    if (
      path.startsWith("blob:") ||
      path.startsWith("data:") ||
      path.startsWith("http://") ||
      path.startsWith("https://")
    ) {
      return path;
    }
    const base = (
      process.env.REACT_APP_API_URL || "http://localhost:8880"
    ).replace(/\/+$/, "");
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `${base}${cleanPath}`;
  };

  const availableSubCategories =
    subCategories && subCategories.length > 0
      ? subCategories
      : DEFAULT_HISTORY_SUB_CATEGORIES;

  // Live preview data
  const previewData = useMemo(() => {
    const selectedCat = availableSubCategories.find(
      (cat) => cat.id === formData.idDanhMuc,
    );
    const imagePreviews = images.map((img) => URL.createObjectURL(img));
    const existingImageUrls = existingImages.map((img) =>
      getFullFileUrl(img.url || img.duong_dan || ""),
    );
    return {
      tieuDe: formData.tieuDe.trim() || "Chưa có tiêu đề",
      tenDiTich: formData.tenDiTich.trim(),
      diaChi: formData.diaChi.trim(),
      moTa: formData.moTa.trim(),
      noiDung: formData.noiDung,
      tags: formData.tags
        ? formData.tags.split(",").map((t) => t.trim()).filter(Boolean)
        : [],
      phanNhom: selectedCat?.name || "",
      previewImages: [...imagePreviews, ...existingImageUrls.filter(Boolean)],
    };
  }, [formData, images, existingImages, availableSubCategories]);

  const coverImage = previewData.previewImages[0] || null;
  const [coverError, setCoverError] = useState(false);

  useEffect(() => {
    setCoverError(false);
  }, [coverImage]);

  const pdfPreviewUrl = useMemo(() => {
    if (file) {
      const isPdf =
        file.type === "application/pdf" ||
        file.name?.toLowerCase().endsWith(".pdf");
      if (isPdf) {
        return URL.createObjectURL(file);
      }
    } else if (existingFiles.length > 0) {
      const f = existingFiles[0];
      const path = f.duong_dan || "";
      if (
        path.toLowerCase().endsWith(".pdf") ||
        f.ten_file?.toLowerCase().endsWith(".pdf")
      ) {
        return getFullFileUrl(path);
      }
    }
    return null;
  }, [file, existingFiles]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    hasUnsavedDataRef.current = true;
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleDateChange = (e) => {
    const raw = e.target.value;
    const cleaned = raw.replace(/[^0-9/]/g, "");
    setDateDisplay(cleaned);

    if (cleaned.length === 10) {
      const parts = cleaned.split("/");
      if (parts.length === 3) {
        const [d, m, y] = parts;
        if (d && m && y && d.length === 2 && m.length === 2 && y.length === 4) {
          setFormData((prev) => ({ ...prev, ngayBanHanh: `${y}-${m}-${d}` }));
          return;
        }
      }
    }
    setFormData((prev) => ({ ...prev, ngayBanHanh: "" }));
  };

  const openDatePicker = () => {
    if (dateInputRef.current) {
      dateInputRef.current.showPicker?.();
      dateInputRef.current.focus();
    }
  };

  const tagsArray = formData.tags
    ? formData.tags.split(",").map((t) => t.trim()).filter(Boolean)
    : [];

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const val = tagInput.trim().slice(0, 20);
      if (!val) return;
      if (tagsArray.length >= 10) return;
      if (tagsArray.some((t) => t.toLowerCase() === val.toLowerCase())) {
        setTagInput("");
        return;
      }
      const updated = [...tagsArray, val].join(", ");
      setFormData((prev) => ({ ...prev, tags: updated }));
      setTagInput("");
      hasUnsavedDataRef.current = true;
    } else if (e.key === "Backspace" && !tagInput && tagsArray.length > 0) {
      const updated = tagsArray.slice(0, -1).join(", ");
      setFormData((prev) => ({ ...prev, tags: updated }));
      hasUnsavedDataRef.current = true;
    }
  };

  const removeTag = (index) => {
    const updated = tagsArray.filter((_, i) => i !== index).join(", ");
    setFormData((prev) => ({ ...prev, tags: updated }));
    hasUnsavedDataRef.current = true;
  };

  const handleContentChange = (value) => {
    setFormData((prev) => ({ ...prev, noiDung: value }));
    hasUnsavedDataRef.current = true;
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      const isPdf = selected.type === "application/pdf" || selected.name.toLowerCase().endsWith(".pdf");
      if (!isPdf) {
        setErrors((prev) => ({ ...prev, file: "Chỉ chấp nhận file định dạng PDF" }));
        return;
      }
      if (selected.size > 20 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, file: "File không được vượt quá 20MB" }));
        return;
      }
      setFile(selected);
      hasUnsavedDataRef.current = true;
      setErrors((prev) => ({ ...prev, file: null }));
    }
  };

  const handleFileDrop = (droppedFile) => {
    if (droppedFile) {
      const isPdf = droppedFile.type === "application/pdf" || droppedFile.name.toLowerCase().endsWith(".pdf");
      if (!isPdf) {
        setErrors((prev) => ({ ...prev, file: "Chỉ chấp nhận file định dạng PDF" }));
        return;
      }
      if (droppedFile.size > 20 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, file: "File không được vượt quá 20MB" }));
        return;
      }
      setFile(droppedFile);
      hasUnsavedDataRef.current = true;
      setErrors((prev) => ({ ...prev, file: null }));
    }
  };

  const handleImagesChange = (e) => {
    const selected = Array.from(e.target.files || []);
    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    const invalid = selected.find((f) => !validTypes.includes(f.type));
    if (invalid) {
      setErrors((prev) => ({ ...prev, images: "Chỉ chấp nhận JPEG, PNG, GIF, WebP" }));
      return;
    }
    if (selected.some((f) => f.size > 10 * 1024 * 1024)) {
      setErrors((prev) => ({ ...prev, images: "Mỗi ảnh không được vượt quá 10MB" }));
      return;
    }
    setImages((prev) => [...prev, ...selected]);
    hasUnsavedDataRef.current = true;
    setErrors((prev) => ({ ...prev, images: null }));
  };

  const removeFile = () => {
    setFile(null);
    setExistingFiles([]);
    hasUnsavedDataRef.current = true;
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.tieuDe.trim()) {
      newErrors.tieuDe = "Vui lòng nhập tiêu đề tài liệu";
    } else if (formData.tieuDe.length > 100) {
      newErrors.tieuDe = "Tiêu đề không được vượt quá 100 ký tự";
    }
    if (!formData.tenDiTich.trim()) {
      newErrors.tenDiTich = "Vui lòng nhập tên di tích";
    } else if (formData.tenDiTich.length > 100) {
      newErrors.tenDiTich = "Tên di tích không được vượt quá 100 ký tự";
    }
    if (!formData.diaChi.trim()) {
      newErrors.diaChi = "Vui lòng nhập địa chỉ";
    } else if (formData.diaChi.length > 100) {
      newErrors.diaChi = "Địa chỉ không được vượt quá 100 ký tự";
    }
    if (!formData.idDanhMuc) {
      newErrors.idDanhMuc = "Vui lòng chọn loại văn bản";
    }
    if (formData.moTa && formData.moTa.length > 300) {
      newErrors.moTa = "Mô tả không được vượt quá 300 ký tự";
    }
    const todayStr = new Date().toISOString().split("T")[0];
    if (formData.ngayBanHanh) {
      if (formData.ngayBanHanh < "1945-01-01") {
        newErrors.ngayBanHanh = "Ngày ban hành không được trước năm 1945";
      } else if (formData.ngayBanHanh > todayStr) {
        newErrors.ngayBanHanh = "Ngày ban hành không được vượt quá ngày hiện tại";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const fd = new FormData();
    fd.append("tieuDe", formData.tieuDe.trim());
    if (formData.tenDiTich.trim()) fd.append("tenDiTich", formData.tenDiTich.trim());
    if (formData.diaChi.trim()) fd.append("diaChi", formData.diaChi.trim());
    if (formData.idDanhMuc) fd.append("idDanhMuc", formData.idDanhMuc);
    if (formData.ngayBanHanh) fd.append("ngayBanHanh", formData.ngayBanHanh);
    fd.append("phamVi", formData.phamVi);
    if (!isEditMode) {
      fd.append("trangThai", "CHO_DUYET");
    }
    if (formData.moTa.trim()) fd.append("moTa", formData.moTa.trim());
    if (formData.noiDung.trim()) fd.append("noiDung", formData.noiDung.trim());
    if (formData.tags.trim()) fd.append("tags", formData.tags.trim());
    if (file) fd.append("file", file);
    images.forEach((img) => fd.append("images", img));

    onSubmit(fd);
  };

  const handleSaveDraft = () => {
    if (!validate()) return;

    const fd = new FormData();
    fd.append("tieuDe", formData.tieuDe.trim() || "Chưa có tiêu đề");
    if (formData.tenDiTich.trim()) fd.append("tenDiTich", formData.tenDiTich.trim());
    if (formData.diaChi.trim()) fd.append("diaChi", formData.diaChi.trim());
    if (formData.idDanhMuc) fd.append("idDanhMuc", formData.idDanhMuc);
    if (formData.ngayBanHanh) fd.append("ngayBanHanh", formData.ngayBanHanh);
    fd.append("phamVi", formData.phamVi);
    if (!isEditMode) {
      fd.append("trangThai", "NHAP");
    }
    if (formData.moTa.trim()) fd.append("moTa", formData.moTa.trim());
    if (formData.noiDung.trim()) fd.append("noiDung", formData.noiDung.trim());
    if (formData.tags.trim()) fd.append("tags", formData.tags.trim());
    if (file) fd.append("file", file);
    images.forEach((img) => fd.append("images", img));

    onSaveDraft(fd);
  };

  const handleBack = () => {
    if (hasUnsavedDataRef.current) {
      setShowLeaveConfirm(true);
      return;
    }
    onBack();
  };

  // Draggable split pane
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = (x / rect.width) * 100;
      setLeftWidth(Math.max(30, Math.min(70, pct)));
    };
    const handleMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Header */}
      <div className="flex items-center gap-4 px-6 py-4 border-b border-gray-200">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Quay lại
        </button>
        <div>
          <h2 className="text-lg font-bold text-gray-900">
            {isEditMode ? "Chỉnh sửa tài liệu" : "Thêm tài liệu mới"}
          </h2>
          <p className="text-sm text-gray-500">Văn hóa - Lịch sử</p>
        </div>
      </div>

      {/* Body: 2-column layout */}
      <div className="p-6">
        <div ref={splitContainerRef} className="flex gap-0">
          {/* ===== LEFT COLUMN: Form ===== */}
          <div style={{ width: `${leftWidth}%` }} className="space-y-4 max-h-[65vh] overflow-y-auto pl-1 pr-2 py-1 flex-shrink-0">
            
            {/* Rejection Alert Banner */}
            {(initialData?.trang_thai === "TU_CHOI" || initialData?.ly_do_tu_choi) && (
              <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800 flex items-start gap-3 shadow-xs">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold text-red-900 block mb-0.5">Lý do từ chối:</span>
                  <span className="text-red-800 leading-relaxed">{initialData.ly_do_tu_choi || "Vui lòng kiểm tra và hoàn thiện lại thông tin tài liệu"}</span>
                </div>
              </div>
            )}

            {/* Tiêu đề */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">
                Tiêu đề <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="tieuDe"
                value={formData.tieuDe}
                maxLength={100}
                onChange={handleChange}
                placeholder="Nhập tiêu đề tài liệu"
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-blue-500 ${
                  errors.tieuDe || formData.tieuDe.length >= 100
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                }`}
              />
              <div className="flex justify-between items-center text-xs mt-1">
                {errors.tieuDe ? (
                  <p className="text-red-500">{errors.tieuDe}</p>
                ) : formData.tieuDe.length >= 100 ? (
                  <p className="text-red-500 font-medium">Đã đạt giới hạn tối đa 100 ký tự</p>
                ) : (
                  <span />
                )}
                <span className={`ml-auto pl-2 ${formData.tieuDe.length >= 100 ? "text-red-500 font-bold" : "text-gray-400"}`}>
                  {formData.tieuDe.length}/100
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Tên di tích */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  Tên di tích <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="tenDiTich"
                  value={formData.tenDiTich}
                  maxLength={100}
                  onChange={handleChange}
                  placeholder="Nhập tên di tích"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-blue-500 ${
                    errors.tenDiTich || formData.tenDiTich.length >= 100
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  }`}
                />
                <div className="flex justify-between items-center text-xs mt-1">
                  {errors.tenDiTich ? (
                    <p className="text-red-500">{errors.tenDiTich}</p>
                  ) : formData.tenDiTich.length >= 100 ? (
                    <p className="text-red-500 font-medium">Đã đạt tối đa 100 ký tự</p>
                  ) : (
                    <span />
                  )}
                  <span className={`ml-auto pl-2 ${formData.tenDiTich.length >= 100 ? "text-red-500 font-bold" : "text-gray-400"}`}>
                    {formData.tenDiTich.length}/100
                  </span>
                </div>
              </div>

              {/* Địa chỉ */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  Địa chỉ <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="diaChi"
                  value={formData.diaChi}
                  maxLength={100}
                  onChange={handleChange}
                  placeholder="Nhập địa chỉ"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-blue-500 ${
                    errors.diaChi || formData.diaChi.length >= 100
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  }`}
                />
                <div className="flex justify-between items-center text-xs mt-1">
                  {errors.diaChi ? (
                    <p className="text-red-500">{errors.diaChi}</p>
                  ) : formData.diaChi.length >= 100 ? (
                    <p className="text-red-500 font-medium">Đã đạt tối đa 100 ký tự</p>
                  ) : (
                    <span />
                  )}
                  <span className={`ml-auto pl-2 ${formData.diaChi.length >= 100 ? "text-red-500 font-bold" : "text-gray-400"}`}>
                    {formData.diaChi.length}/100
                  </span>
                </div>
              </div>
            </div>

            {/* Loại văn bản */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">
                Loại văn bản <span className="text-red-500">*</span>
              </label>
              <ManageableDocTypeSelect
                name="idDanhMuc"
                value={formData.idDanhMuc}
                onChange={handleChange}
                initialDocTypes={availableSubCategories}
                defaultList={DEFAULT_HISTORY_SUB_CATEGORIES}
                storageKey="custom_history_subcategories_v3"
                title="Thêm loại văn bản mới"
                placeholder="-- Chọn loại văn bản --"
                addPlaceholder="Nhập tên loại văn bản mới..."
                searchPlaceholder="Tìm loại văn bản..."
                emptyText="Không tìm thấy loại văn bản nào"
                itemLabel="loại văn bản"
                error={errors.idDanhMuc}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Ngày ban hành */}
              <div className="w-full min-w-0">
                <label className="block text-sm font-bold text-gray-700 mb-1">Ngày ban hành</label>
                <VietnameseDatePicker
                  className="w-full"
                  value={formData.ngayBanHanh}
                  minDate="1945-01-01"
                  maxDate={new Date().toISOString().split("T")[0]}
                  error={!!errors.ngayBanHanh}
                  onChange={(isoVal, displayVal) => {
                    setFormData((prev) => ({ ...prev, ngayBanHanh: isoVal }));
                    setDateDisplay(displayVal);
                    if (errors.ngayBanHanh) setErrors((prev) => ({ ...prev, ngayBanHanh: null }));
                    hasUnsavedDataRef.current = true;
                  }}
                  placeholder="dd/MM/yyyy"
                />
                {errors.ngayBanHanh && <p className="text-red-500 text-xs mt-1">{errors.ngayBanHanh}</p>}
              </div>

              {/* Phạm vi */}
              <div className="w-full min-w-0">
                <label className="block text-sm font-bold text-gray-700 mb-1">Phạm vi</label>
                <CustomSelect
                  name="phamVi"
                  value={formData.phamVi}
                  onChange={handleChange}
                  options={PHAM_VI_OPTIONS}
                />
              </div>
            </div>

            {/* Mô tả */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Mô tả</label>
              <textarea
                name="moTa"
                value={formData.moTa}
                maxLength={300}
                onChange={handleChange}
                rows={3}
                placeholder="Nhập mô tả tài liệu..."
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-blue-500 resize-none ${
                  errors.moTa || formData.moTa.length >= 300
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                }`}
              />
              <div className="flex justify-between items-center text-xs mt-1">
                {errors.moTa ? (
                  <p className="text-red-500">{errors.moTa}</p>
                ) : formData.moTa.length >= 300 ? (
                  <p className="text-red-500 font-medium">Đã đạt tối đa 300 ký tự</p>
                ) : (
                  <span />
                )}
                <span className={`ml-auto pl-2 ${formData.moTa.length >= 300 ? "text-red-500 font-bold" : "text-gray-400"}`}>
                  {formData.moTa.length}/300
                </span>
              </div>
            </div>

            {/* Nội dung tài liệu */}
            <div>
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <label className="block text-sm font-bold text-gray-700">
                  Nội dung tài liệu
                </label>
                <div className="inline-flex p-1 bg-gray-100/90 rounded-lg text-xs font-medium border border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      if (file || existingFiles.length > 0) return;
                      setContentInputMode("editor");
                    }}
                    disabled={!!(file || existingFiles.length > 0)}
                    title={
                      file || existingFiles.length > 0
                        ? "Vui lòng xóa file đính kèm trước khi chuyển sang soạn thảo văn bản"
                        : "Soạn thảo văn bản trực tiếp"
                    }
                    className={`px-3 py-1.5 rounded-md transition-all flex items-center gap-1.5 ${
                      contentInputMode === "editor"
                        ? "bg-white text-blue-600 font-semibold shadow-xs"
                        : file || existingFiles.length > 0
                        ? "text-gray-400 cursor-not-allowed opacity-50"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    Soạn thảo văn bản
                  </button>
                  <button
                    type="button"
                    onClick={() => setContentInputMode("file")}
                    className={`px-3 py-1.5 rounded-md transition-all flex items-center gap-1.5 ${
                      contentInputMode === "file"
                        ? "bg-white text-blue-600 font-semibold shadow-xs"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    Tải file PDF
                    {(file || existingFiles.length > 0) && (
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    )}
                  </button>
                </div>
              </div>

              {contentInputMode === "editor" ? (
                <>
                  <style>{`
                    .ql-toolbar.ql-snow {
                      display: flex !important;
                      flex-wrap: wrap !important;
                      overflow-x: visible !important;
                      border-top-left-radius: 8px !important;
                      border-top-right-radius: 8px !important;
                      padding: 8px !important;
                      border-color: #d1d5db !important;
                    }
                    .ql-container.ql-snow {
                      border-bottom-left-radius: 8px !important;
                      border-bottom-right-radius: 8px !important;
                      border-color: #d1d5db !important;
                      font-size: 14px !important;
                    }
                    .ql-editor {
                      min-height: 160px !important;
                      max-height: 300px !important;
                      overflow-y: auto !important;
                    }
                    .ql-editor.ql-blank::before {
                      color: #9ca3af !important;
                      font-style: normal !important;
                    }
                  `}</style>
                  <div className="border rounded-md overflow-hidden">
                    <ReactQuill
                      theme="snow"
                      value={formData.noiDung}
                      onChange={handleContentChange}
                      modules={QUILL_MODULES}
                      formats={QUILL_FORMATS}
                      placeholder="Nhập nội dung chi tiết của tài liệu..."
                      className="bg-white"
                    />
                  </div>
                </>
              ) : (
                <div>
                  {!file && existingFiles.length === 0 ? (
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDraggingFile(true);
                      }}
                      onDragLeave={() => setIsDraggingFile(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDraggingFile(false);
                        if (e.dataTransfer.files?.[0]) {
                          handleFileDrop(e.dataTransfer.files[0]);
                        }
                      }}
                      onClick={() => fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                        isDraggingFile
                          ? "border-blue-500 bg-blue-50/70 ring-2 ring-blue-200"
                          : "border-gray-300 hover:border-blue-400 hover:bg-slate-50/70 bg-white"
                      }`}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-3 shadow-xs">
                        <UploadCloud className="w-6 h-6" />
                      </div>
                      <p className="text-sm font-semibold text-gray-800 mb-1">
                        Kéo & thả file PDF vào đây, hoặc{" "}
                        <span className="text-blue-600 hover:underline font-normal">chọn từ máy tính</span>
                      </p>
                      <p className="text-xs text-gray-500">
                        Hỗ trợ định dạng: <span className="font-semibold text-gray-700">PDF</span> (Tối đa 20MB)
                      </p>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-r from-blue-50/60 to-indigo-50/40 border border-blue-200/80 rounded-xl p-4 shadow-xs">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3.5 min-w-0">
                          <div className="w-11 h-11 bg-red-100 text-red-700 rounded-xl flex items-center justify-center font-bold text-xs uppercase flex-shrink-0 shadow-xs border border-red-200">
                            {file?.name ? file.name.split(".").pop() : "PDF"}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-bold text-gray-900 truncate max-w-[280px]">
                                {file?.name || fixMojibake(existingFiles[0]?.ten_file)}
                              </p>
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 flex-shrink-0">
                                <FileCheck className="w-3 h-3" />
                                Đã sẵn sàng
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Dung lượng:{" "}
                              {file
                                ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
                                : existingFiles[0]?.kich_thuoc_mb
                                  ? `${existingFiles[0]?.kich_thuoc_mb} MB`
                                  : "---"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-white hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors flex items-center gap-1 shadow-xs"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                            Đổi file
                          </button>
                          <button
                            type="button"
                            onClick={removeFile}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-200"
                            title="Xóa file"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </div>
                  )}
                  {errors.file && <p className="text-red-500 text-xs mt-1.5">{errors.file}</p>}
                </div>
              )}
            </div>

            {/* Tags */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-bold text-gray-700">Tags</label>
                <span className={`text-xs ${tagsArray.length >= 10 ? "text-red-500 font-bold" : "text-gray-400"}`}>
                  {tagsArray.length}/10 tag
                </span>
              </div>
              <div
                className={`flex flex-wrap items-center gap-1.5 px-3 py-2 border rounded-lg text-sm bg-white min-h-[42px] cursor-text transition-colors ${
                  tagsArray.length >= 10
                    ? "border-gray-300 focus-within:ring-2 focus-within:ring-red-400"
                    : "border-gray-300 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500"
                }`}
                onClick={() => document.getElementById("tag-input")?.focus()}
              >
                {tagsArray.map((tag, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeTag(idx);
                      }}
                      className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full hover:bg-blue-200 transition-colors"
                    >
                      <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
                <input
                  id="tag-input"
                  type="text"
                  maxLength={20}
                  disabled={tagsArray.length >= 10}
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder={
                    tagsArray.length >= 10
                      ? "Đã đạt tối đa 10 tag"
                      : tagsArray.length === 0
                      ? "Nhập tag (tối đa 20 ký tự) và nhấn Enter..."
                      : "Thêm tag..."
                  }
                  className="flex-1 min-w-[120px] border-none outline-none text-sm bg-transparent p-0 disabled:cursor-not-allowed"
                />
              </div>
              <div className="flex justify-between items-center text-xs text-gray-400 mt-1">
                <span>
                  {tagsArray.length >= 10 ? (
                    <span className="text-red-500 font-medium">Đã đạt giới hạn tối đa 10 tag</span>
                  ) : tagInput.length >= 20 ? (
                    <span className="text-red-500 font-medium">Đã đạt tối đa 20 ký tự cho tag này</span>
                  ) : (
                    "Tối đa 10 tag, mỗi tag tối đa 20 ký tự (Gõ tag và nhấn Enter để thêm)"
                  )}
                </span>
                {tagInput && tagsArray.length < 10 && (
                  <span className={`ml-auto pl-2 ${tagInput.length >= 20 ? "text-red-500 font-bold" : "text-gray-400"}`}>
                    {tagInput.length}/20 ký tự
                  </span>
                )}
              </div>
            </div>

            {/* Hình ảnh */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-bold text-gray-700">
                  Hình ảnh tài liệu <span className="text-xs font-normal text-gray-500">(JPEG, PNG, GIF, WebP - Tối đa 10MB/ảnh)</span>
                </label>
                {(images.length > 0 || existingImages.length > 0) && (
                  <span className="text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full">
                    {images.length + existingImages.length} hình ảnh
                  </span>
                )}
              </div>

              {/* Upload Dropzone */}
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDraggingImages(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setIsDraggingImages(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDraggingImages(false);
                  if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                    handleImagesChange({ target: { files: e.dataTransfer.files } });
                  }
                }}
                onClick={() => imagesInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all duration-150 ${
                  isDraggingImages
                    ? "border-blue-500 bg-blue-50/80 scale-[0.99]"
                    : "border-gray-300 hover:border-blue-400 bg-gray-50/60 hover:bg-blue-50/30"
                }`}
              >
                <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-blue-100/80 text-blue-600 flex items-center justify-center shadow-xs">
                  <UploadCloud className="w-5 h-5" />
                </div>
                <p className="text-sm font-semibold text-gray-700">
                  Kéo & thả hình ảnh vào đây hoặc <span className="text-blue-600 hover:underline font-normal">chọn từ máy tính</span>
                </p>
                <p className="text-xs text-gray-400 mt-0.5">Hỗ trợ chọn hoặc kéo thả nhiều hình ảnh cùng lúc</p>
                <input
                  ref={imagesInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  multiple
                  onChange={handleImagesChange}
                  className="hidden"
                />
              </div>

              {/* Image Preview Gallery Cards */}
              {(existingImages.length > 0 || images.length > 0) && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mt-3">
                  {/* Existing images from server */}
                  {existingImages.map((img, idx) => (
                    <div
                      key={img.id || idx}
                      className="relative group rounded-xl border border-gray-200 bg-white p-2 shadow-xs hover:shadow-md transition-all overflow-hidden flex flex-col"
                    >
                      <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100 mb-1.5">
                        <img
                          src={getFullFileUrl(img.url || img.duong_dan)}
                          alt={img.ten_file_goc || "Ảnh hiện có"}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                        <span className="absolute top-1 left-1 bg-black/60 text-white text-[10px] font-medium px-1.5 py-0.5 rounded backdrop-blur-xs">
                          Hiện có
                        </span>
                      </div>
                      <p className="text-xs text-gray-700 font-medium truncate" title={img.ten_file_goc}>
                        {img.ten_file_goc || `Ảnh #${idx + 1}`}
                      </p>
                    </div>
                  ))}

                  {/* Newly selected images */}
                  {images.map((img, idx) => {
                    const objectUrl = URL.createObjectURL(img);
                    const sizeMb = (img.size / (1024 * 1024)).toFixed(2);
                    return (
                      <div
                        key={idx}
                        className="relative group rounded-xl border border-blue-200 bg-blue-50/20 p-2 shadow-xs hover:shadow-md transition-all overflow-hidden flex flex-col"
                      >
                        <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100 mb-1.5">
                          <img
                            src={objectUrl}
                            alt={img.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                          />
                          <span className="absolute top-1 left-1 bg-blue-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded shadow-xs">
                            Mới
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeImage(idx);
                            }}
                            className="absolute top-1 right-1 p-1 bg-red-600 hover:bg-red-700 text-white rounded-md shadow-xs opacity-90 hover:opacity-100 transition-opacity cursor-pointer"
                            title="Xóa ảnh này"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="flex items-center justify-between gap-1 min-w-0 text-xs">
                          <span className="text-gray-800 font-medium truncate" title={img.name}>
                            {img.name}
                          </span>
                          <span className="text-gray-400 flex-shrink-0 text-[10px]">{sizeMb} MB</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {errors.images && <p className="text-red-500 text-xs mt-1.5">{errors.images}</p>}
            </div>
          </div>

          {/* ===== DRAG HANDLE ===== */}
          <div
            className="flex-shrink-0 w-2 cursor-col-resize relative group flex items-center justify-center"
            onMouseDown={(e) => {
              isDragging.current = true;
              document.body.style.cursor = "col-resize";
              document.body.style.userSelect = "none";
              e.preventDefault();
            }}
          >
            <div className="w-1 h-full bg-gray-200 group-hover:bg-blue-400 group-active:bg-blue-500 transition-colors rounded-full" />
          </div>

          {/* ===== RIGHT COLUMN: Live Preview ===== */}
          <div style={{ flex: 1 }} className="bg-slate-50 rounded-xl overflow-hidden border border-gray-200 max-h-[65vh] overflow-y-auto min-w-0 p-6">
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-6">
              {/* Hero Header Card */}
              <div className="flex flex-col sm:flex-row gap-6 items-start border-b border-gray-100 pb-6">
                {/* Book Cover */}
                <div className="flex-shrink-0">
                  {coverImage && !coverError ? (
                    <img
                      src={coverImage}
                      alt="Ảnh bìa tài liệu"
                      className="w-[160px] h-[220px] rounded-xl object-cover border border-gray-200 shadow-md"
                      onError={() => setCoverError(true)}
                    />
                  ) : (
                    <div className="w-[160px] h-[220px] bg-gradient-to-br from-slate-800 to-slate-950 rounded-xl border border-slate-700 flex flex-col items-center justify-center p-3 text-center shadow-md">
                      <Book className="w-12 h-12 text-blue-300 mb-2" />
                      <span className="text-xs text-blue-200 font-medium">Chưa có ảnh bìa</span>
                    </div>
                  )}
                </div>

                {/* Info Body */}
                <div className="flex-1 flex flex-col justify-center min-w-0">
                  <span className="inline-block px-3 py-1 rounded-md text-xs font-bold bg-blue-600 text-white w-fit mb-2.5 shadow-xs">
                    {previewData.phanNhom || "Văn hóa - Lịch sử"}
                  </span>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight mb-2.5 break-words [overflow-wrap:anywhere]">
                    {previewData.tieuDe}
                  </h1>

                  {(previewData.tenDiTich || previewData.diaChi) && (
                    <div className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-600 mb-2">
                      <MapPin className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <span className="truncate">
                        {previewData.tenDiTich}
                        {previewData.diaChi ? ` - ${previewData.diaChi}` : ""}
                      </span>
                    </div>
                  )}

                  {formData.ngayBanHanh && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2.5">
                      <Calendar className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                      <span>Ngày ban hành: {dateDisplay || formData.ngayBanHanh}</span>
                    </div>
                  )}

                  {previewData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3.5">
                      {previewData.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all shadow-xs"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Tải về
                    </button>
                  </div>
                </div>
              </div>

              {/* Main Content Body */}
              <div>
                {/* Giới thiệu */}
                {previewData.moTa && (
                  <div className="mb-6">
                    <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <FileText className="w-4 h-4 text-blue-600" />
                      Giới thiệu
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-700 leading-relaxed whitespace-pre-line bg-slate-50/80 p-4 rounded-xl border border-slate-200/80">
                      {previewData.moTa}
                    </p>
                  </div>
                )}

                {/* Nội dung chi tiết */}
                {previewData.noiDung && contentInputMode === "editor" && (
                  <div className="mb-6">
                    <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <BookOpen className="w-4 h-4 text-blue-600" />
                      Nội dung chi tiết
                    </h3>
                    <div
                      className="text-xs sm:text-sm text-gray-800 leading-relaxed bg-white p-5 rounded-xl border border-slate-200 prose max-w-none shadow-xs [&_img]:max-w-full [&_img]:rounded-lg max-h-[400px] overflow-y-auto"
                      dangerouslySetInnerHTML={{ __html: previewData.noiDung }}
                    />
                  </div>
                )}

                {/* Xem trước tài liệu văn bản / File đính kèm */}
                {pdfPreviewUrl ? (
                  <div className="mb-6 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between gap-3 mb-2.5">
                      <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5 text-blue-600" />
                        Xem trước tài liệu văn bản
                      </h3>
                      <a
                        href={pdfPreviewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-xs"
                        title="Mở toàn màn hình trong tab mới"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Mở rộng
                      </a>
                    </div>
                    <div className="border border-slate-300 rounded-xl overflow-hidden shadow-xs bg-slate-800">
                      <iframe
                        src={`${pdfPreviewUrl}#toolbar=1&navpanes=0`}
                        className="w-full h-[480px] border-0 bg-white"
                        title="PDF Live Preview"
                      />
                    </div>
                  </div>
                ) : file || existingFiles.length > 0 ? (
                  <div className="mb-6 pt-3 border-t border-gray-200">
                    <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5 text-blue-600" />
                      Tài liệu đính kèm
                    </h3>
                    <div className="bg-gradient-to-r from-blue-50/70 to-slate-50 border border-blue-200/80 rounded-xl p-4 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-11 h-11 bg-blue-100 text-blue-700 rounded-xl flex items-center justify-center font-bold text-xs uppercase flex-shrink-0 border border-blue-200">
                          {file?.name?.split(".").pop() || existingFiles[0]?.ten_file?.split(".").pop() || "DOC"}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm font-bold text-gray-900 truncate">
                            {file?.name || fixMojibake(existingFiles[0]?.ten_file) || "Tài liệu văn bản"}
                          </p>
                          <p className="text-[11px] text-gray-500 mt-0.5">
                            Dung lượng: {file ? `${(file.size / (1024 * 1024)).toFixed(2)} MB` : existingFiles[0]?.kich_thuoc_mb ? `${existingFiles[0]?.kich_thuoc_mb} MB` : "---"}
                          </p>
                          <p className="text-[11px] text-amber-700 mt-0.5">
                            ℹ️ File định dạng Word sẽ được hệ thống xử lý và hiển thị trực tuyến khi xuất bản.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : !previewData.noiDung && !previewData.moTa ? (
                  <div className="text-center py-8 text-gray-400">
                    <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-xs">Chưa có nội dung hoặc tệp đính kèm</p>
                  </div>
                ) : null}

                {/* Hình ảnh tư liệu */}
                {previewData.previewImages.length > 1 && (
                  <div className="mb-6 pt-3 border-t border-gray-200">
                    <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                      <ImageIcon className="w-3.5 h-3.5 text-blue-600" />
                      Hình ảnh tư liệu ({previewData.previewImages.length})
                    </h3>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                      {previewData.previewImages.map((src, idx) => (
                        <div key={idx} className="aspect-video rounded-lg overflow-hidden border border-gray-200 bg-gray-100 shadow-2xs group">
                          <img
                            src={src}
                            alt={`Hình ảnh ${idx + 1}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200">
        <button
          type="button"
          onClick={handleBack}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Hủy
        </button>
        {!isEditMode && (
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            Lưu nháp
          </button>
        )}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
        >
          {isLoading && (
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
          {isEditMode ? "Cập nhật" : "Tạo tài liệu"}
        </button>
      </div>

      <ConfirmModal
        isOpen={showLeaveConfirm}
        onClose={() => {
          setShowLeaveConfirm(false);
          onBack();
        }}
        onConfirm={() => setShowLeaveConfirm(false)}
        title="Rời khỏi trang tạo tài liệu"
        message="Bạn có chắc chắn muốn quay lại? Dữ liệu bạn vừa nhập chưa được lưu sẽ bị mất."
        confirmText="Ở lại"
        cancelText="Rời khỏi"
        type="warning"
        confirmType="warning"
      />
    </div>
  );
}
